import { useCallback, useMemo, useState } from 'react';
import { UseFormReset } from 'react-hook-form';

import { AttributeStatus } from 'companySettings/enums';
import {
  useGetPartFinderAttributeNames,
  useGetPublishedPartFinderAttributes,
  usePartFinderAttributeActions,
} from 'companySettings/hooks';
import { IPartFinderAttribute, IPartFinderProductTypeAttribute } from 'companySettings/types';
import { NotificationPosition } from 'core/enums';
import { FormProvider } from 'core/states';

import { mapAttributeFormDefaults, mapAttributeFromForm } from './mappers';
import { upsertProductTypeAttributeSchema } from './schema';
import {
  AttributeValueSource,
  IInheritedAttributeSource,
  IUpsertProductTypeAttributeFormData,
} from './types';
import { UpsertProductTypeAttributeFormFields } from './UpsertProductTypeAttributeFormFields';
import { copyListValues } from './utils';

interface IProps {
  attribute?: IPartFinderProductTypeAttribute;
  // Set for an attribute a family inherits: only its values are edited.
  inheritedSource?: IInheritedAttributeSource;
  existingNames: string[];
  isOpen: boolean;
  onClose: () => void;
  onSaved: (attribute: IPartFinderProductTypeAttribute) => void;
}

// The New / Edit Attribute drawer of a product type. The attribute goes back to the product type
// form and is saved with it; only a new value list asked for with "Save the values as a list" is
// created here, published, when the drawer is saved.
export const UpsertProductTypeAttribute = ({
  attribute,
  inheritedSource,
  existingNames,
  isOpen,
  onClose,
  onSaved,
}: IProps) => {
  const [createdLists, setCreatedLists] = useState<IPartFinderAttribute[]>([]);

  const { records: publishedLists = [] } = useGetPublishedPartFinderAttributes(isOpen);
  const { data: attributeNames = [] } = useGetPartFinderAttributeNames(isOpen);
  const { savePartFinderAttribute } = usePartFinderAttributeActions(NotificationPosition.FORM);

  // A list created from this drawer is offered at once, before the published lists refetch.
  const lists = useMemo(
    () => [
      ...createdLists.filter(
        (createdList) => !publishedLists.some((list) => list.id === createdList.id)
      ),
      ...publishedLists,
    ],
    [createdLists, publishedLists]
  );

  const defaultValues = useMemo(() => mapAttributeFormDefaults(attribute), [attribute]);

  const handleOnClose = useCallback(
    (reset?: UseFormReset<IUpsertProductTypeAttributeFormData>) => {
      reset?.(defaultValues);
      onClose();
    },
    [defaultValues, onClose]
  );

  const onSubmit = useCallback(
    (reset: UseFormReset<IUpsertProductTypeAttributeFormData>) =>
      async (data: IUpsertProductTypeAttributeFormData) => {
        // An inherited attribute keeps its name, type and source: only its values change, and
        // whether the new ones go back to the product type.
        if (inheritedSource) {
          onSaved({
            ...attribute,
            values: data.values ?? [],
            updateList: Boolean(data.updateList),
          });
          handleOnClose(reset);

          return;
        }

        let createdList: IPartFinderAttribute | undefined;

        if (AttributeValueSource.TYPED === data.source && data.saveAsList) {
          const { success, partFinderAttribute: list } = await savePartFinderAttribute({
            name: data.newListName.trim(),
            status: AttributeStatus.PUBLISHED,
            type: data.type,
            values: copyListValues(data.values),
          });

          if (!success || !list?.id) {
            return;
          }

          createdList = list;
          setCreatedLists((current) => [list, ...current]);
        }

        onSaved(mapAttributeFromForm(data, lists, attribute, createdList));
        handleOnClose(reset);
      },
    [attribute, handleOnClose, inheritedSource, lists, onSaved, savePartFinderAttribute]
  );

  return (
    <FormProvider
      schema={upsertProductTypeAttributeSchema()}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <UpsertProductTypeAttributeFormFields
        attribute={attribute}
        inheritedSource={inheritedSource}
        lists={lists}
        attributeNames={attributeNames.map(({ name }) => name)}
        existingNames={existingNames}
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
      />
    </FormProvider>
  );
};
